// Iframe-native fetch bypass — extracted from CheckoutPage.js so other
// modules (admin order editor, etc.) can reuse the same workaround.
//
// Why this exists:
// The Emergent preview loader (`assets.emergent.sh/scripts/emergent-main.js`),
// PostHog session-replay, and some browser extensions monkey-patch
// `window.fetch`. When our code then does `resp.json()` or `resp.clone()`,
// Safari/Chrome throw "Body is disturbed or locked" / "body stream already
// read" because the wrapper has already consumed the stream.
//
// Workaround: pull the original native fetch from a hidden same-origin iframe.
// The iframe gets its own window with its own untouched native fetch. We bind
// it to the outer window so cookies / CORS still behave normally.

var _NATIVE_FETCH = null;

export function getNativeFetch() {
  if (_NATIVE_FETCH) return _NATIVE_FETCH;
  try {
    var ifr = document.createElement('iframe');
    ifr.style.display = 'none';
    ifr.setAttribute('aria-hidden', 'true');
    ifr.setAttribute('tabindex', '-1');
    document.body.appendChild(ifr);
    var f = ifr.contentWindow && ifr.contentWindow.fetch;
    if (f) {
      // Keep the iframe alive — removing it can invalidate the associated
      // fetch reference in some browsers.
      _NATIVE_FETCH = f.bind(window);
      return _NATIVE_FETCH;
    }
  } catch (_e) { /* cross-origin / sandboxed — fall back */ }
  _NATIVE_FETCH = window.fetch.bind(window);
  return _NATIVE_FETCH;
}

// Defensive JSON fetch — uses the iframe-sourced native fetch so global
// fetch-wrappers cannot consume our response body. Falls back to clone()
// on the wrapped fetch if the native one isn't available.
//
// Also races every call against a 10s timeout because in some preview
// environments emergent-main.js wraps iframes too and the resulting promise
// never settles (neither resolves nor rejects). The race lets us fall back
// to plain fetch instead of hanging indefinitely.
export async function fetchJSON(url, opts) {
  var timeoutMs = (opts && opts.timeoutMs) || 10000;
  function timed(p) {
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () { reject(new Error('timeout')); }, timeoutMs);
      p.then(function (v) { clearTimeout(t); resolve(v); },
             function (e) { clearTimeout(t); reject(e); });
    });
  }
  var nf = getNativeFetch();
  var resp;
  try {
    resp = await timed(nf(url, opts || {}));
  } catch (_e1) {
    // Native fetch hung or errored — try the (possibly wrapped) global fetch
    try {
      resp = await timed(fetch(url, opts || {}));
    } catch (_e2) {
      return { ok: false, status: 0, data: null, raw: '', error: 'all-fetch-paths-failed' };
    }
  }
  var text = '';
  try {
    text = await timed(resp.clone().text());
  } catch (_e3) {
    try { text = await timed(resp.text()); } catch (_e4) { text = ''; }
  }
  var data = null;
  if (text) {
    try { data = JSON.parse(text); } catch (_e5) { data = null; }
  }
  return { ok: resp.ok, status: resp.status, data: data, raw: text };
}

// XMLHttpRequest-based JSON fetch — XHR is wrapped far less commonly than
// `fetch`. Emergent's preview loader intercepts fetch but not XHR. Use this
// when fetchJSON (iframe-native fetch) returns a promise that never settles
// in the preview environment, which we observed in OrderEditPanel's catalog
// load. Resolves to `{ ok, status, data, raw }` like fetchJSON.
//
// Uses addEventListener instead of .onload/.onerror because some 3rd-party
// wrappers (Datadog RUM, PostHog, emergent-main.js) overwrite the bare
// setters but leave addEventListener alone.
export function xhrJSON(url, opts) {
  return new Promise(function (resolve) {
    var settled = false;
    function done(result) {
      if (settled) return;
      settled = true;
      resolve(result);
    }
    try {
      var xhr = new XMLHttpRequest();
      xhr.addEventListener('load', function () {
        var raw = xhr.responseText || '';
        var data = null;
        if (raw) { try { data = JSON.parse(raw); } catch (_e) { data = null; } }
        done({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: data, raw: raw });
      });
      xhr.addEventListener('error', function () {
        done({ ok: false, status: 0, data: null, raw: '', networkError: true });
      });
      xhr.addEventListener('timeout', function () {
        done({ ok: false, status: 0, data: null, raw: '', timeout: true });
      });
      xhr.addEventListener('abort', function () {
        done({ ok: false, status: 0, data: null, raw: '', aborted: true });
      });
      xhr.open((opts && opts.method) || 'GET', url, true);
      var headers = (opts && opts.headers) || {};
      Object.keys(headers).forEach(function (h) {
        try { xhr.setRequestHeader(h, headers[h]); } catch (_e) { /* invalid header — skip */ }
      });
      xhr.timeout = (opts && opts.timeoutMs) || 30000;
      xhr.send((opts && opts.body) || null);
    } catch (e) {
      done({ ok: false, status: 0, data: null, raw: '', error: String(e) });
    }
  });
}
